const AVAILABILITIES = 'availabilities';
const SECTORS = 'sectors';

function isDisplayOnly(type) {
    return type === 'static_text' || type === 'static_image';
}

function isEmptyAnswer(value) {
    if (value === undefined || value === null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === 'object')
        return Object.keys(value).length === 0;
    return false;
}

/**
 * Whether a required answer is present. Availabilities are stored under the
 * redux-form key `availabilities`, not under the field id.
 *
 * @param {object} formLayout
 * @param {object} field
 * @param {Record<string, unknown>} formValues
 * @returns {boolean}
 */
function fieldAnswerFilled(formLayout, field, formValues) {
    if (field.type === AVAILABILITIES)
        return !isEmptyAnswer(formValues?.availabilities);

    const groupId = field.groupId ?? field.group_id;
    if (!groupId)
        return !isEmptyAnswer(formValues?.[field.id]);

    const group = (formLayout?.groups || []).find((item) => item.id === groupId);
    const prefix = `grp_${groupId}`;
    if (!group?.multiple) {
        const row = formValues?.[prefix];
        if (!row || typeof row !== 'object' || Array.isArray(row))
            return false;
        return !isEmptyAnswer(row[field.id]);
    }

    const rows = Array.isArray(formValues?.[prefix]) ? formValues[prefix] : [];
    const minRows = Math.max(1, Number(group.minItems ?? group.min_items) || 1);
    for (let i = 0; i < minRows; i += 1) {
        const row = rows[i];
        if (!row || typeof row !== 'object' || isEmptyAnswer(row[field.id]))
            return false;
    }
    return true;
}

/**
 * Candidature form can be submitted: required fields are filled, sectors are
 * chosen when that widget is present, and availabilities are set only when
 * that widget is present.
 *
 * @param {object|null|undefined} formLayout
 * @param {Record<string, unknown>|null|undefined} formValues
 * @returns {boolean}
 */
export function isApplicationFormReady(formLayout, formValues) {
    const fields = formLayout?.fields || [];
    const values = formValues || {};

    for (const field of fields) {
        if (!field || isDisplayOnly(field.type) || field.type === 'hidden')
            continue;
        if (field.type === AVAILABILITIES || field.type === SECTORS) {
            if (!fieldAnswerFilled(formLayout, field, values))
                return false;
            continue;
        }
        if (!field.required)
            continue;
        if (!fieldAnswerFilled(formLayout, field, values))
            return false;
    }
    return true;
}

/**
 * True when the stored application belongs to this user (or has no owner yet).
 *
 * @param {object|null|undefined} application
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function applicationBelongsToUser(application, user) {
    if (!application || !user?.id)
        return false;
    const ownerId = application.user?.id ?? application.user_id;
    if (ownerId == null || ownerId === '')
        return true;
    return String(ownerId) === String(user.id);
}
